import React, { useState, useEffect, useMemo } from 'react';
import {
    Box,
    Typography,
    Card,
    Button,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Chip,
    IconButton,
    Skeleton,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Slide,
    Paper as MuiPaper,
    Collapse,
    CircularProgress,
    Alert,
    Tooltip,
} from '@mui/material';
import {
    Visibility as EyeIcon,
    Print as PrintIcon,
    Close as CloseIcon,
    ReceiptLong as ReceiptIcon,
    Bolt as BoltIcon,
    ExpandMore as ExpandMoreIcon,
    ExpandLess as ExpandLessIcon,
    Business as BusinessIcon,
    PrecisionManufacturing as MachineIcon,
    Refresh as RefreshIcon,
} from '@mui/icons-material';
import { formatToIST } from '../../utils/dataUtils';
import billingService from '../../services/billingService';
import { machineLabel } from '../../utils/machineLabel';

const inr = (n) => '₹' + Number(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 });

const BillingPage = () => {
    const [loading, setLoading] = useState(true);
    const [invoices, setInvoices] = useState([]);
    const [expanded, setExpanded] = useState(() => new Set());

    // Invoice preview modal
    const [selectedInvoice, setSelectedInvoice] = useState(null);
    const [templateHtml, setTemplateHtml] = useState('');
    const [showInvoiceModal, setShowInvoiceModal] = useState(false);
    const [modalLoading, setModalLoading] = useState(false);
    const [modalError, setModalError] = useState('');

    // Check & Create Billing dialog
    const [billOpen, setBillOpen] = useState(false);
    const [billLoading, setBillLoading] = useState(false);
    const [billPreview, setBillPreview] = useState(null);
    const [billError, setBillError] = useState('');
    const [billGenerating, setBillGenerating] = useState(false);
    const [billDone, setBillDone] = useState(null);

    const fetchInvoices = async () => {
        try {
            setLoading(true);
            const res = await billingService.getInvoices();
            setInvoices(res.data || res || []);
        } catch (err) {
            console.error('Failed to fetch billing data:', err);
            setInvoices([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { fetchInvoices(); }, []);

    // Group invoices: one "full rental invoice" per contract, with the
    // individual per-machine invoices underneath.
    const groups = useMemo(() => {
        const m = new Map();
        invoices.forEach(inv => {
            const key = inv.rental_contract_id || inv.id || 'Unknown';
            if (!m.has(key)) {
                m.set(key, { contractId: key, customer: inv.contact_name || inv.customer || '—', machines: [], total: 0, unpaid: 0, latest: inv.bill_date });
            }
            const g = m.get(key);
            g.machines.push(inv);
            g.total += parseFloat(inv.amount) || 0;
            if ((inv.status || '').toLowerCase() !== 'paid') g.unpaid += 1;
            if (inv.bill_date && (!g.latest || new Date(inv.bill_date) > new Date(g.latest))) g.latest = inv.bill_date;
        });
        return Array.from(m.values()).sort((a, b) => new Date(b.latest || 0) - new Date(a.latest || 0));
    }, [invoices]);

    const toggle = (id) => setExpanded(prev => {
        const next = new Set(prev);
        if (next.has(id)) next.delete(id); else next.add(id);
        return next;
    });

    const openBilling = async () => {
        setBillOpen(true); setBillError(''); setBillDone(null); setBillPreview(null); setBillLoading(true);
        try {
            const res = await billingService.previewBilling();
            setBillPreview(res.data || res);
        } catch (e) {
            setBillError(e.response?.data?.message || e.message || 'Failed to check billing.');
        } finally {
            setBillLoading(false);
        }
    };

    const handleGenerate = async () => {
        setBillGenerating(true); setBillError('');
        try {
            const res = await billingService.generateBilling();
            const d = res.data || res;
            setBillDone(d);
            await fetchInvoices();
        } catch (e) {
            setBillError(e.response?.data?.message || e.message || 'Failed to generate billing.');
        } finally {
            setBillGenerating(false);
        }
    };

    const handleViewInvoice = async (invoiceId) => {
        setModalLoading(true);
        setModalError('');
        setTemplateHtml('');
        setShowInvoiceModal(true);
        try {
            const response = await billingService.getInvoiceDetail(invoiceId);
            if (response.success) {
                const { invoice, template } = response.data;
                setSelectedInvoice(invoice);

                let rendered = template?.template_data;
                if (!rendered) {
                    setModalError('Template data not found. Please ensure invoice templates are available in the database.');
                    setModalLoading(false);
                    return;
                }

                let totalAmount = 0;
                let totalQty = 0;
                let itemsHtml = '';

                if (invoice.line_items_json) {
                    const items = typeof invoice.line_items_json === 'string'
                        ? JSON.parse(invoice.line_items_json)
                        : invoice.line_items_json;

                    items.forEach((item, idx) => {
                        const amt = parseFloat(item.amount) || 0;
                        const qty = parseFloat(item.qty) || 0;
                        totalAmount += amt;
                        totalQty += qty;
                        itemsHtml += `
                            <tr style="border-bottom: 1px solid #000;">
                                <td style="border-right: 1px solid #000; padding: 5px; text-align: center;">${idx + 1}</td>
                                <td style="border-right: 1px solid #000; padding: 5px; text-align: center;">${item.model || ''}<br>${item.serial || ''}</td>
                                <td style="border-right: 1px solid #000; padding: 5px; text-align: center;">9973</td>
                                <td style="border-right: 1px solid #000; padding: 5px; text-align: left;">${item.description}</td>
                                <td style="border-right: 1px solid #000; padding: 5px; text-align: center;">Nos</td>
                                <td style="border-right: 1px solid #000; padding: 5px; text-align: center;">${qty}</td>
                                <td style="border-right: 1px solid #000; padding: 5px; text-align: right;">${parseFloat(item.rate).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</td>
                                <td style="border-right: 1px solid #000; padding: 5px; text-align: right;">${amt.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</td>
                                <td style="border-right: 1px solid #000; padding: 5px; text-align: right;">0.00</td>
                                <td style="border-right: 1px solid #000; padding: 5px; text-align: center;">18%</td>
                                <td style="border-right: 1px solid #000; padding: 5px; text-align: right;">${(amt * 0.09).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</td>
                                <td style="border-right: 1px solid #000; padding: 5px; text-align: right;">${(amt * 0.09).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</td>
                                <td style="padding: 5px; text-align: right;">0.00</td>
                            </tr>
                        `;
                    });
                }

                const cgst = totalAmount * 0.09;
                const sgst = totalAmount * 0.09;
                const grandTotal = totalAmount + cgst + sgst;
                const formatNum = (num) => num.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
                const numberToWords = (amount) => "INR " + formatNum(amount) + " Only";

                rendered = rendered.replace(/{{branch}}/g, invoice.branch || 'Padur');
                rendered = rendered.replace(/{{billed_to_name}}/g, invoice.contact_name || invoice.customer || '-');
                rendered = rendered.replace(/{{billed_to_address}}/g, invoice.billed_to_address || '-');
                rendered = rendered.replace(/{{billed_to_pin}}/g, '');
                rendered = rendered.replace(/{{billed_to_state}}/g, 'TAMILNADU');
                rendered = rendered.replace(/{{billed_to_state_code}}/g, '33');
                rendered = rendered.replace(/{{billed_to_gstin}}/g, '');
                rendered = rendered.replace(/{{billed_to_pan}}/g, '');
                rendered = rendered.replace(/{{ship_to_name}}/g, invoice.contact_name || invoice.customer || '-');
                rendered = rendered.replace(/{{ship_to_address}}/g, invoice.ship_to_address || invoice.billed_to_address || '-');
                rendered = rendered.replace(/{{ship_to_state}}/g, 'TAMILNADU');
                rendered = rendered.replace(/{{ship_to_state_code}}/g, '33');
                rendered = rendered.replace(/{{ship_to_pin}}/g, '');
                rendered = rendered.replace(/{{ship_to_gstin}}/g, '');
                rendered = rendered.replace(/{{bill_no}}/g, invoice.bill_no || '-');
                rendered = rendered.replace(/{{bill_date}}/g, invoice.bill_date ? formatToIST(invoice.bill_date).split(',')[0] : '-');
                rendered = rendered.replace(/{{dc_no}}/g, invoice.dc_no || '-');
                rendered = rendered.replace(/{{dc_date}}/g, invoice.dc_date ? formatToIST(invoice.dc_date).split(',')[0] : '-');
                rendered = rendered.replace(/{{lr_date}}/g, invoice.lr_date ? formatToIST(invoice.lr_date).split(',')[0] : '-');
                rendered = rendered.replace(/{{transporter}}/g, invoice.transporter || '-');
                rendered = rendered.replace(/{{buyer_order_no}}/g, invoice.buyer_order_no || '-');
                rendered = rendered.replace(/{{vehicle_no}}/g, invoice.vehicle_no || '-');
                rendered = rendered.replace(/{{buyer_order_dt}}/g, invoice.buyer_order_date ? formatToIST(invoice.buyer_order_date).split(',')[0] : '-');
                rendered = rendered.replace(/{{contact_name}}/g, invoice.contact_name || '-');
                rendered = rendered.replace(/{{contact_phone}}/g, invoice.contact_phone || '-');
                rendered = rendered.replace(/{{qr_code_image}}/g, '-');
                rendered = rendered.replace(/{{items_table_rows}}/g, itemsHtml);
                rendered = rendered.replace(/{{total_qty}}/g, totalQty.toString());
                rendered = rendered.replace(/{{total_taxable_amount}}/g, formatNum(totalAmount));
                rendered = rendered.replace(/{{total_cgst}}/g, formatNum(cgst));
                rendered = rendered.replace(/{{total_sgst}}/g, formatNum(sgst));
                rendered = rendered.replace(/{{total_igst}}/g, '0.00');
                rendered = rendered.replace(/{{amount_in_words}}/g, numberToWords(Math.round(grandTotal)));
                rendered = rendered.replace(/{{total_taxable_amount_formatted}}/g, formatNum(totalAmount));
                rendered = rendered.replace(/{{total_cgst_formatted}}/g, formatNum(cgst));
                rendered = rendered.replace(/{{total_sgst_formatted}}/g, formatNum(sgst));
                rendered = rendered.replace(/{{total_igst_formatted}}/g, '0.00');
                rendered = rendered.replace(/{{grand_total_formatted}}/g, formatNum(grandTotal));
                rendered = rendered.replace(/{{authorized_signatory}}/g, invoice.authorized_signatory || 'JENNY MODI');
                rendered = rendered.replace(/{{irn_no}}/g, invoice.irn_no || '-');
                rendered = rendered.replace(/{{ack_no}}/g, '');
                rendered = rendered.replace(/{{ack_date}}/g, invoice.ack_no_date || '');

                setTemplateHtml(rendered);
            }
        } catch (error) {
            console.error("Error showing invoice:", error);
            setModalError('Failed to load invoice preview. Please try again.');
        } finally {
            setModalLoading(false);
        }
    };

    const StatusChip = ({ status }) => (
        <Chip label={status} size="small" sx={{
            fontWeight: 700, height: 22,
            bgcolor: (status || '').toLowerCase() === 'paid' ? '#dcfce7' : '#fee2e2',
            color: (status || '').toLowerCase() === 'paid' ? '#15803d' : '#b91c1c',
        }} />
    );

    return (
        <Box p={3} className="animate-fade-in">
            {/* Check & Create Billing — no page title, the top bar already names the page */}
            <Box display="flex" justifyContent="flex-end" alignItems="center" mb={3} flexWrap="wrap" gap={2}>
                <Button
                    variant="contained" size="large" startIcon={<BoltIcon />}
                    onClick={openBilling}
                    sx={{ borderRadius: '12px', textTransform: 'none', fontWeight: 800, px: 3,
                        bgcolor: '#059669', '&:hover': { bgcolor: '#047857' } }}
                >
                    Check &amp; Create Billing
                </Button>
            </Box>

            {/* Grouped invoices: rental → machines */}
            {loading ? (
                [...Array(3)].map((_, i) => <Skeleton key={i} variant="rectangular" height={70} sx={{ mb: 2, borderRadius: 3 }} />)
            ) : groups.length === 0 ? (
                <Card sx={{ p: 6, textAlign: 'center', borderRadius: 3 }}>
                    <ReceiptIcon sx={{ fontSize: 56, color: '#cbd5e1', mb: 1 }} />
                    <Typography variant="h6" fontWeight={700} color="text.secondary">No invoices yet</Typography>
                    <Typography variant="body2" color="text.secondary">Use “Check &amp; Create Billing” to generate this month’s invoices.</Typography>
                </Card>
            ) : (
                groups.map((g) => {
                    const isOpen = expanded.has(g.contractId);
                    return (
                        <Card key={g.contractId} sx={{ mb: 2, borderRadius: 3, overflow: 'hidden', border: '1px solid #e2e8f0', boxShadow: 'none' }}>
                            {/* Full rental invoice header */}
                            <Box onClick={() => toggle(g.contractId)}
                                sx={{ p: 2, display: 'flex', alignItems: 'center', gap: 1.5, cursor: 'pointer', bgcolor: isOpen ? '#f8fafc' : '#fff', '&:hover': { bgcolor: '#f8fafc' } }}>
                                <IconButton size="small">{isOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}</IconButton>
                                <BusinessIcon sx={{ color: '#2563eb' }} />
                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                    <Typography variant="subtitle1" fontWeight={800} noWrap>{g.contractId}</Typography>
                                    <Typography variant="caption" color="text.secondary" noWrap>{g.customer}</Typography>
                                </Box>
                                <Chip label={`${g.machines.length} machine${g.machines.length > 1 ? 's' : ''}`} size="small"
                                    sx={{ fontWeight: 700, bgcolor: '#eff6ff', color: '#1d4ed8' }} />
                                <Box sx={{ textAlign: 'right', minWidth: 110 }}>
                                    <Typography variant="caption" color="text.secondary" display="block">Total (taxable)</Typography>
                                    <Typography variant="subtitle1" fontWeight={800}>{inr(g.total)}</Typography>
                                </Box>
                                <StatusChip status={g.unpaid > 0 ? `${g.unpaid} Unpaid` : 'Paid'} />
                            </Box>

                            {/* Per-machine invoices */}
                            <Collapse in={isOpen} unmountOnExit>
                                <TableContainer component={Box} sx={{ px: 1, pb: 1, bgcolor: '#fbfcfe' }}>
                                    <Table size="small">
                                        <TableHead>
                                            <TableRow>
                                                <TableCell sx={{ fontWeight: 700, color: 'text.secondary' }}>Bill No</TableCell>
                                                <TableCell sx={{ fontWeight: 700, color: 'text.secondary' }}>Machine</TableCell>
                                                <TableCell sx={{ fontWeight: 700, color: 'text.secondary' }}>Date</TableCell>
                                                <TableCell sx={{ fontWeight: 700, color: 'text.secondary' }}>Amount</TableCell>
                                                <TableCell sx={{ fontWeight: 700, color: 'text.secondary' }}>Status</TableCell>
                                                <TableCell align="right" sx={{ fontWeight: 700, color: 'text.secondary' }}>Action</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {g.machines.map((inv) => (
                                                <TableRow key={inv.invoice_id} hover>
                                                    <TableCell sx={{ fontWeight: 700, fontFamily: 'monospace', fontSize: '0.78rem' }}>{inv.bill_no || inv.id}</TableCell>
                                                    <TableCell>
                                                        <Box display="flex" alignItems="center" gap={0.75}>
                                                            <MachineIcon sx={{ fontSize: 16, color: '#64748b' }} />
                                                            <Typography variant="body2" fontWeight={600}>{machineLabel(inv)}</Typography>
                                                        </Box>
                                                    </TableCell>
                                                    <TableCell sx={{ color: 'text.secondary' }}>{inv.date}</TableCell>
                                                    <TableCell sx={{ fontWeight: 800 }}>{inr(inv.amount)}</TableCell>
                                                    <TableCell><StatusChip status={inv.status} /></TableCell>
                                                    <TableCell align="right">
                                                        <Tooltip title="View invoice">
                                                            <Button variant="outlined" size="small" startIcon={<EyeIcon />}
                                                                onClick={() => handleViewInvoice(inv.invoice_id)}
                                                                sx={{ borderRadius: '8px', textTransform: 'none', fontWeight: 600 }}>
                                                                View
                                                            </Button>
                                                        </Tooltip>
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            </Collapse>
                        </Card>
                    );
                })
            )}

            {/* Check & Create Billing dialog */}
            <Dialog open={billOpen} onClose={() => !billGenerating && setBillOpen(false)} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
                <DialogTitle sx={{ fontWeight: 800, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <BoltIcon sx={{ color: '#059669' }} /> Check &amp; Create Billing
                </DialogTitle>
                <DialogContent dividers>
                    {billLoading ? (
                        <Box sx={{ py: 5, textAlign: 'center' }}>
                            <CircularProgress />
                            <Typography sx={{ mt: 2 }} color="text.secondary">Checking what would be billed…</Typography>
                        </Box>
                    ) : billError ? (
                        <Alert severity="error" sx={{ borderRadius: 2 }}>{billError}</Alert>
                    ) : billDone !== null ? (
                        <Box>
                            <Alert severity="success" sx={{ borderRadius: 2 }}>
                                ✅ Generated and emailed <b>{billDone.count ?? 0}</b> invoice{(billDone.count ?? 0) === 1 ? '' : 's'}. They now appear in the list.
                            </Alert>
                            {billDone.needs_rate_count > 0 && (
                                <Alert severity="warning" sx={{ borderRadius: 2, mt: 1 }}>
                                    <b>{billDone.needs_rate_count}</b> machine(s) were skipped because they have <b>no rate set</b>. Set a rate on those machines in the rental contract, then re-run billing to invoice them.
                                </Alert>
                            )}
                        </Box>
                    ) : billPreview ? (
                        <>
                            <Box display="flex" gap={2} flexWrap="wrap" mb={2}>
                                <Chip label={`Billing month: ${billPreview.billing_month || '—'}`} sx={{ fontWeight: 700, bgcolor: '#eff6ff', color: '#1d4ed8' }} />
                                <Chip label={`${billPreview.billable_count ?? billPreview.count} ready to bill`} sx={{ fontWeight: 700, bgcolor: '#ecfdf5', color: '#047857' }} />
                                {billPreview.needs_rate_count > 0 && (
                                    <Chip label={`${billPreview.needs_rate_count} need a rate`} sx={{ fontWeight: 700, bgcolor: '#fee2e2', color: '#b91c1c' }} />
                                )}
                                <Chip label={`Total ${inr(billPreview.total_amount)}`} sx={{ fontWeight: 700, bgcolor: '#fef3c7', color: '#b45309' }} />
                            </Box>
                            {(billPreview.invoices || []).length === 0 ? (
                                <Alert severity="warning" sx={{ borderRadius: 2 }}>
                                    No machines on any active contract for this billing month. (Billing covers the previous month; contracts that started this month bill next month.)
                                </Alert>
                            ) : (
                                <>
                                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                        Every machine on every active contract this month is listed below — one invoice per machine. Machines marked <b style={{ color: '#b91c1c' }}>“set rate”</b> have no rate and won’t be billed until you set one.
                                    </Typography>
                                    <TableContainer component={Box} sx={{ maxHeight: 340 }}>
                                        <Table size="small" stickyHeader>
                                            <TableHead>
                                                <TableRow>
                                                    <TableCell sx={{ fontWeight: 700 }}>Contract</TableCell>
                                                    <TableCell sx={{ fontWeight: 700 }}>Machine</TableCell>
                                                    <TableCell sx={{ fontWeight: 700 }}>Period</TableCell>
                                                    <TableCell sx={{ fontWeight: 700 }}>Days</TableCell>
                                                    <TableCell align="right" sx={{ fontWeight: 700 }}>Total</TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {[...(billPreview.invoices || [])]
                                                    .sort((a, b) => String(a.contract).localeCompare(String(b.contract)))
                                                    .map((p, i) => (
                                                    <TableRow key={i} sx={p.needs_rate ? { bgcolor: '#fff7f7' } : undefined}>
                                                        <TableCell sx={{ fontSize: '0.78rem', fontWeight: 600 }}>{p.contract}</TableCell>
                                                        <TableCell>{machineLabel(p)}</TableCell>
                                                        <TableCell sx={{ fontSize: '0.78rem' }}>{p.period}</TableCell>
                                                        <TableCell>{p.days}</TableCell>
                                                        <TableCell align="right" sx={{ fontWeight: 700 }}>
                                                            {p.needs_rate
                                                                ? <Chip size="small" label="set rate" sx={{ fontWeight: 700, bgcolor: '#fee2e2', color: '#b91c1c' }} />
                                                                : inr(p.grand_total)}
                                                        </TableCell>
                                                    </TableRow>
                                                ))}
                                            </TableBody>
                                        </Table>
                                    </TableContainer>
                                </>
                            )}
                        </>
                    ) : null}
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={() => setBillOpen(false)} disabled={billGenerating} sx={{ textTransform: 'none', fontWeight: 700 }}>
                        {billDone !== null ? 'Done' : 'Cancel'}
                    </Button>
                    {billDone === null && (
                        <>
                            <Button onClick={openBilling} disabled={billLoading || billGenerating} startIcon={<RefreshIcon />} sx={{ textTransform: 'none', fontWeight: 700 }}>
                                Re-check
                            </Button>
                            <Button variant="contained" onClick={handleGenerate}
                                disabled={billLoading || billGenerating || !billPreview || (billPreview.billable_count ?? billPreview.count) === 0}
                                startIcon={billGenerating ? <CircularProgress size={16} sx={{ color: '#fff' }} /> : <BoltIcon />}
                                sx={{ textTransform: 'none', fontWeight: 800, borderRadius: '10px', bgcolor: '#059669', '&:hover': { bgcolor: '#047857' } }}>
                                {billGenerating ? 'Generating…' : `Generate & Send (${billPreview?.billable_count ?? billPreview?.count ?? 0})`}
                            </Button>
                        </>
                    )}
                </DialogActions>
            </Dialog>

            {/* Invoice Preview Modal */}
            <Dialog open={showInvoiceModal} onClose={() => setShowInvoiceModal(false)} maxWidth="lg" fullWidth TransitionComponent={Slide} direction="up">
                <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="h6" fontWeight={700}>Invoice Preview: {selectedInvoice?.bill_no}</Typography>
                    <IconButton onClick={() => setShowInvoiceModal(false)}><CloseIcon /></IconButton>
                </DialogTitle>
                <DialogContent dividers sx={{ bgcolor: '#f1f5f9', minHeight: '500px' }}>
                    {modalLoading ? (
                        <Box>
                            <Skeleton variant="rectangular" height={60} sx={{ mb: 2 }} />
                            <Skeleton variant="rectangular" height={100} sx={{ mb: 2 }} />
                            <Skeleton variant="rectangular" height={200} sx={{ mb: 2 }} />
                            <Skeleton variant="rectangular" height={80} />
                        </Box>
                    ) : modalError ? (
                        <Box sx={{ textAlign: 'center', py: 6 }}>
                            <Typography variant="h6" color="error" gutterBottom>⚠️ Error Loading Invoice</Typography>
                            <Typography color="text.secondary">{modalError}</Typography>
                            <Button variant="outlined" sx={{ mt: 2 }} onClick={() => handleViewInvoice(selectedInvoice?.invoice_id)}>Retry</Button>
                        </Box>
                    ) : templateHtml ? (
                        <MuiPaper sx={{ p: 0, overflow: 'auto' }}>
                            <div dangerouslySetInnerHTML={{ __html: templateHtml }} />
                        </MuiPaper>
                    ) : (
                        <Box sx={{ textAlign: 'center', py: 6 }}>
                            <Typography color="text.secondary">No invoice content to display.</Typography>
                        </Box>
                    )}
                </DialogContent>
                <DialogActions sx={{ p: 2 }}>
                    <Button onClick={() => setShowInvoiceModal(false)}>Close</Button>
                    <Button variant="contained" startIcon={<PrintIcon />}
                        onClick={() => window.open(`${process.env.REACT_APP_API_URL || 'https://api.mtandtiot.in/api'}/billing/invoices/${selectedInvoice?.invoice_id}/download`, '_blank')}>
                        Print PDF
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default BillingPage;
